import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "What's in this package" list in step with the option pickers.
 *
 * The list itself is computed in Liquid, which already gets it right - fetching
 * the product page for a specific variant returns exactly the parts that variant
 * contains. The problem is only that Horizon's variant change swaps a fixed set
 * of elements out of the fetched section and this block is not one of them, so
 * without this the list stays frozen on the default variant while the pickers
 * move. On the Cummins package that means someone selects the intercooler guard
 * and the $379 part never appears in the list they are reading.
 *
 * So: take the server's own version out of the fetched HTML and morph it in. No
 * gating logic is duplicated here - Liquid stays the single source of truth.
 *
 * @extends {Component<{}>}
 */
class PackageContentsComponent extends Component {
  #target = null;

  connectedCallback() {
    super.connectedCallback();
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {{ promise?: Promise<{detail?: {html?: Document, productId?: string}}> }} event */
  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        // A combined listing can swap in a different product entirely; leave it
        // to the section re-render rather than half-updating someone else's list.
        if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector('package-contents-component');
        if (!next) return;
        morph(this, next);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[package-contents] update failed:', error);
      });
  };
}

if (!customElements.get('package-contents-component')) {
  customElements.define('package-contents-component', PackageContentsComponent);
}
